import { PageParams } from "@/types/pagination";

import {
  EmailContactType,
  EmailDirection,
  EmailParticipantRole,
} from "./enums/email";

// ==================== Legacy Enum (kept for backwards compatibility) ====================

export type EmailStatus =
  | "FAILED"
  | "SENT"
  | "PROCESSING"
  | "OPENED"
  | "CLICKED";

// ==================== New Email Types ====================

export type EmailMessageStatus =
  | "DRAFT"
  | "QUEUED"
  | "SENDING"
  | "SENT"
  | "DELIVERED"
  | "RECEIVED"
  | "FAILED"
  | "BOUNCED"
  | "COMPLAINED";

export { EmailContactType, EmailDirection, EmailParticipantRole };

export type EmailFolder =
  | "inbox"
  | "sent"
  | "drafts"
  | "starred"
  | "archived"
  | "all"
  | "system";

export type EmailThreadScope = "provider" | "clinic";

/** How a draft was composed, so it resumes in the matching composer. */
export type EmailDraftKind = "NEW" | "REPLY" | "FORWARD";

/** What kind of mail a message is — mirrors the backend EmailMessageType enum. */
export type EmailMessageType =
  | "CUSTOM"
  | "INVOICE"
  | "TENANT_CREATED"
  | "PROVIDER_INVITE"
  | "PURCHASE_ORDER"
  | "RECEIPT"
  | "NOTIFICATION"
  | "WEBHOOK_FAILED"
  | "FORM"
  | "BLAST"
  | "SEQUENCE";

// ==================== Contact ====================

export interface EmailPatientRef {
  id: string;
  name: string;
}

export interface EmailContact {
  id: string;
  email: string | null;
  displayName: string | null;
  contactType: EmailContactType;
  patientIds: string[]; // M2M relationship - contact can be linked to multiple patients
  patients: EmailPatientRef[]; // Linked patients with display names (parallels patientIds)
}

// ==================== Participant ====================

export interface EmailParticipant {
  id: string;
  role: EmailParticipantRole;
  contact: EmailContact;
}

// ==================== Attachment ====================

export interface EmailAttachment {
  id: string;
  filename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  isInline: boolean;
  contentId: string | null;
}

// ==================== Attachment URLs ====================

export interface AttachmentUrlInfo {
  attachmentId: string;
  signedUrl: string;
}

export interface MessageAttachmentUrls {
  /** Map of content_id -> attachment URL info (for inline images with cid: references) */
  byContentId: Record<string, AttachmentUrlInfo>;
  /** Map of attachment_id -> signed URL (for all downloadable attachments) */
  byAttachmentId: Record<string, string>;
  /** When the signed URLs expire (ISO 8601 format) */
  expiresAt: string;
}

// ==================== Message ====================

export interface EmailSenderRef {
  id: string;
  name: string;
}

export interface EmailMessage {
  id: string;
  threadId: string;
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  snippet: string | null;
  status: EmailMessageStatus;
  direction: EmailDirection;
  messageType: EmailMessageType;
  sentDate: string | null;
  deliveredDate: string | null;
  isRead: boolean;
  isStarred: boolean;
  isDraft: boolean;
  draftKind: EmailDraftKind | null;
  /** Drafts only: the message a reply/forward draft answers. */
  parentMessageId: string | null;
  sentBy: EmailSenderRef | null;
  participants: EmailParticipant[];
  attachments: EmailAttachment[];
  // Engagement metrics
  openCount: number;
  uniqueOpenCount: number;
  clickCount: number;
  uniqueClickCount: number;
  firstOpenedDate: string | null;
  lastOpenedDate: string | null;
  createdDate: string;
  updatedDate: string | null;
}

// ==================== Thread ====================

export interface EmailThreadListItem {
  id: string;
  subject: string | null;
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  firstMessageDate: string | null;
  lastMessageDate: string | null;
  messageCount: number;
  fromContact: EmailContact | null;
  toContacts: EmailContact[];
  sentBy: EmailSenderRef | null;
  // Reply-state signals derived from the thread's messages.
  lastMessageDirection: EmailDirection | null;
  awaitingReply: boolean;
  hasAttachments: boolean;
  // Representative linked patient for patient-linked display/filtering.
  primaryPatientId: string | null;
  // Thread-level engagement (summed across messages)
  openCount: number;
  clickCount: number;
  // Most recent open across the thread's messages (for engagement hover detail).
  lastOpenedDate: string | null;
  createdDate: string;
  updatedDate: string | null;
}

export interface EmailThread {
  id: string;
  subject: string | null;
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  firstMessageDate: string | null;
  lastMessageDate: string | null;
  messages: EmailMessage[];
  sentBy: EmailSenderRef | null;
  createdDate: string;
  updatedDate: string | null;
}

// ==================== Request/Response Types ====================

export interface EmailFetchParams extends PageParams {
  folder?: EmailFolder;
  search?: string;
  beforeDate?: string;
  afterDate?: string;
  cursor?: string;
  scope?: EmailThreadScope;
  // Clinic mailbox filtering params.
  triage?: boolean;
  unread?: boolean;
  starred?: boolean;
  hasPatient?: boolean;
  hasAttachment?: boolean;
  /** Only threads with an email sent by this provider id. */
  sentBy?: string;
  groupByPatient?: boolean;
}

export interface EmailFolderCounts {
  inboxUnread: number;
  starred: number;
  drafts: number;
}

export interface EmailThreadListResponse {
  threads: EmailThreadListItem[];
  total: number;
  page: number;
  perPage: number;
  hasMore: boolean;
}

export interface ComposeEmailPayload {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  patientId?: string;
  senderScope?: EmailThreadScope;
  /** Per-message override of the sending address local-part (before "@"). Omit to use the clinic default. */
  fromLocalPart?: string;
  /** Per-message override of the sender display name. Omit to use the clinic default. */
  senderName?: string;
}

export interface ReplyEmailPayload {
  bodyHtml?: string;
  bodyText?: string;
  toOverride?: string[];
  cc?: string[];
  bcc?: string[];
  senderScope?: EmailThreadScope;
  /** Per-message override of the sending address local-part (before "@"). Omit to use the clinic default. */
  fromLocalPart?: string;
  /** Per-message override of the sender display name. Omit to use the clinic default. */
  senderName?: string;
}

export interface ForwardEmailPayload {
  to: string[];
  cc?: string[];
  bcc?: string[];
  bodyHtml?: string;
  bodyText?: string;
  /** The message being forwarded. Omit to forward the thread's latest message. */
  messageId?: string;
  /** Attachments of the forwarded message to send along with it. */
  forwardAttachmentIds?: string[];
  senderScope?: EmailThreadScope;
  /** Per-message override of the sending address local-part (before "@"). Omit to use the clinic default. */
  fromLocalPart?: string;
  /** Per-message override of the sender display name. Omit to use the clinic default. */
  senderName?: string;
}

export interface CreateDraftPayload {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  threadId?: string;
  parentMessageId?: string;
  draftKind?: EmailDraftKind;
}

export interface UpdateDraftPayload {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
}

export interface ThreadActionResponse {
  success: boolean;
  threadId: string;
  action: string;
  newValue: boolean;
}

export interface SendEmailResponse {
  success: boolean;
  messageId?: string;
  threadId?: string;
  error?: string;
  // Full server representations for response-driven caching (no invalidation).
  // Populated by the reply endpoint; compose/forward omit them for now.
  message?: EmailMessage;
  threadListItem?: EmailThreadListItem;
}

export interface EmailEngagement {
  messageId: string;
  openCount: number;
  uniqueOpenCount: number;
  clickCount: number;
  uniqueClickCount: number;
  firstOpenedDate: string | null;
  lastOpenedDate: string | null;
  firstClickedDate: string | null;
  lastClickedDate: string | null;
}

// ==================== UI State Types ====================

export interface EmailFilters {
  folder: EmailFolder;
  search: string;
  page: number;
  perPage: number;
}

export const DEFAULT_EMAIL_FILTERS: EmailFilters = {
  folder: "inbox",
  search: "",
  page: 1,
  perPage: 50,
};

// ==================== Helper Functions ====================

export function getStatusLabel(status: EmailMessageStatus): string {
  const labels: Record<EmailMessageStatus, string> = {
    DRAFT: "Draft",
    QUEUED: "Queued",
    SENDING: "Sending",
    SENT: "Sent",
    DELIVERED: "Delivered",
    RECEIVED: "Received",
    FAILED: "Failed",
    BOUNCED: "Bounced",
    COMPLAINED: "Complained",
  };
  return labels[status] || status;
}

export function getStatusColor(
  status: EmailMessageStatus,
): "success" | "error" | "warning" | "info" {
  switch (status) {
    case "DELIVERED":
    case "RECEIVED":
      return "success";
    case "FAILED":
    case "BOUNCED":
    case "COMPLAINED":
      return "error";
    case "QUEUED":
    case "SENDING":
      return "warning";
    default:
      return "info";
  }
}

export function getFolderLabel(folder: EmailFolder): string {
  const labels: Record<EmailFolder, string> = {
    inbox: "Inbox",
    sent: "Sent",
    drafts: "Drafts",
    starred: "Starred",
    archived: "Archived",
    all: "All Mail",
    system: "System Emails",
  };
  return labels[folder] || folder;
}

export function formatParticipants(participants: EmailParticipant[]): string {
  return participants
    .filter((p) => p.role === "TO")
    .map((p) => p.contact.displayName || p.contact.email || "Unknown")
    .join(", ");
}

export function getFromParticipant(
  participants: EmailParticipant[],
): EmailContact | null {
  const from = participants.find((p) => p.role === EmailParticipantRole.FROM);
  return from?.contact || null;
}

export function getToContact(
  participants: EmailParticipant[],
): EmailContact | null {
  const to = participants.find((p) => p.role === EmailParticipantRole.TO);
  return to?.contact || null;
}
